import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type GenderStringEnum = "male" | "female";

type DoSportStringEnum = "yes" | "no" | "stopped";

type HypoglycaemiaStringEnum = "yes" | "no" | "unknown";

type SevereHypoglycaemiaStringEnum = "yes" | "no" | "unknown";

type HyperglycaemiaStringEnum = "yes" | "no" | "unknown";

type SevereHyperglycaemiaStringEnum = "yes" | "no" | "unknown";

type HospitalisationStringEnum = "yes" | "no" | "unknown";

type NeuropathieStringEnum = "yes" | "no" | "unknown";

type ChronicRenalFailureStringEnum = "yes" | "no" | "unknown";

type AlbuminuriaStringEnum = "yes" | "no" | "unknown";

type McvaStringEnum = "yes" | "no" | "unknown";

type HeartFailureStringEnum = "yes" | "no" | "unknown";

type SevereComorbidityStringEnum = "yes" | "no" | "unknown";

type HtaStringEnum = "treated" | "yes" | "no" | "unknown";

export default class DiabetowiseDischargeCompletedJournalEvent extends JournalEvent {
  static override type = "diabetowise-discharge-completed-journal-event";

  /**
   * The last name of the patient
   */
  @attr("string") declare lastName: string;

  /**
   * The first name of the patient
   */
  @attr("string") declare firstName: string;

  /**
   * L'âge du patient
   */
  @attr("number") declare age: number;

  /**
   * Le sexe du patient
   */
  @attr("string") declare gender: GenderStringEnum;

  /**
   * has progressive or life-threatening disease in the short term
   */
  @attr("boolean", {
    allowNull: true
  })
  declare prognosticLifeThreatening: boolean;

  /**
   * the patient is a smoker
   */
  @attr("boolean", {
    allowNull: true
  })
  declare smoker: boolean;

  /**
   * Patient's weight in kg
   */
  @attr("number") declare weight: number;

  /**
   * Patient's height in cm
   */
  @attr("number") declare height: number;

  /**
   * the patient do sport
   */
  @attr("string") declare doSport: DoSportStringEnum;

  /**
   * Phone number of the patient
   */
  @attr("string") declare phone: string;

  /**
   * the hba1c in percent
   */
  @attr("number") declare hba1c: number;

  /**
   * take metformine
   */
  @attr("boolean", {
    allowNull: true
  })
  declare met: boolean;

  /**
   * take isglt2
   */
  @attr("boolean", {
    allowNull: true
  })
  declare isglt2: boolean;

  /**
   * take AR GLP-1
   */
  @attr("boolean", {
    allowNull: true
  })
  declare arglp1: boolean;

  /**
   * take insulin
   */
  @attr("boolean", {
    allowNull: true
  })
  declare insulin: boolean;

  /**
   * take idpp4
   */
  @attr("boolean", {
    allowNull: true
  })
  declare idpp4: boolean;

  /**
   * has had hypoglycaemia in the past year
   */
  @attr("string") declare hypoglycaemia: HypoglycaemiaStringEnum;

  /**
   * has had severe hypoglycaemia in the past year
   */
  @attr("string") declare severeHypoglycaemia: SevereHypoglycaemiaStringEnum;

  /**
   * has had hyperglycaemia in the past year
   */
  @attr("string") declare hyperglycaemia: HyperglycaemiaStringEnum;

  /**
   * has had severe hyperglycaemia in the past year
   */
  @attr("string") declare severeHyperglycaemia: SevereHyperglycaemiaStringEnum;

  /**
   * Hospitalisation for heart failure, acute renal failure/dialysis, atheromatous cardiovascular disease in the past year
   */
  @attr("string") declare hospitalisation: HospitalisationStringEnum;

  @attr("string") declare neuropathie: NeuropathieStringEnum;

  @attr("string") declare chronicRenalFailure: ChronicRenalFailureStringEnum;

  /**
   * DFG formule CKD-EPI (en mL/min/1.73m^2)
   */
  @attr("number") declare dfg: number;

  /**
   * albuminuria in the past year
   */
  @attr("string") declare albuminuria: AlbuminuriaStringEnum;

  /**
   * Maladie cardiovasculaire athéromateurs
   */
  @attr("string") declare mcva: McvaStringEnum;

  /**
   * RAC in mg/g
   */
  @attr("number") declare rac: number;

  /**
   * heart failure in the past yea
   */
  @attr("string") declare heartFailure: HeartFailureStringEnum;

  /**
   * FEVG in %
   */
  @attr("number") declare fevg: number;

  /**
   * other severes comorbidity in the past year
   */
  @attr("string") declare severeComorbidity: SevereComorbidityStringEnum;

  /**
   * hta in the past year
   */
  @attr("string") declare hta: HtaStringEnum;

  /**
   * last systolic in consultation
   */
  @attr("number") declare systolic: number;

  /**
   * last diastolic in consultation
   */
  @attr("number") declare diastolic: number;

  /**
   * dyslipidemia in the past year
   */
  @attr("number") declare dyslipidemia: number;

  static override validations(intl: IntlService) {
    return {
      lastName: validatePresence({
        presence: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.last-name"
        )
      }),
      firstName: validatePresence({
        presence: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.first-name"
        )
      }),
      age: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.age"
          )
        }),
        validateNumber({
          integer: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.age"
          )
        })
      ],
      gender: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.gender"
          )
        }),
        validateInclusion({
          list: ["male", "female"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.gender"
          )
        })
      ],
      weight: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.weight"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.weight"
          )
        })
      ],
      height: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.height"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.height"
          )
        })
      ],
      doSport: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.do-sport"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "stopped"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.do-sport"
          )
        })
      ],
      phone: validatePresence({
        presence: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.phone"
        )
      }),
      hba1c: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hba1c"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hba1c"
          )
        })
      ],
      hypoglycaemia: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hypoglycaemia"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hypoglycaemia"
          )
        })
      ],
      hyperglycaemia: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hyperglycaemia"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hyperglycaemia"
          )
        })
      ],
      hospitalisation: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hospitalisation"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hospitalisation"
          )
        })
      ],
      neuropathie: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.neuropathie"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.neuropathie"
          )
        })
      ],
      chronicRenalFailure: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.chronic-renal-failure"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.chronic-renal-failure"
          )
        })
      ],
      albuminuria: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.albuminuria"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.albuminuria"
          )
        })
      ],
      mcva: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.mcva"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.mcva"
          )
        })
      ],
      heartFailure: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.heart-failure"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.heart-failure"
          )
        })
      ],
      severeComorbidity: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.severe-comorbidity"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.severe-comorbidity"
          )
        })
      ],
      hta: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hta"
          )
        }),
        validateInclusion({
          list: ["treated", "yes", "no", "unknown"],
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.hta"
          )
        })
      ],
      dyslipidemia: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.dyslipidemia"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabetowise-discharge-completed-journal-event.dyslipidemia"
          )
        })
      ],
      prognosticLifeThreatening: validatePresence({
        presence: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.prognostic-life-threatening"
        )
      }),
      smoker: validatePresence({
        presence: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.smoker"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.diabetowise-discharge-completed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      dfg: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.dfg"
        )
      }),
      rac: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.rac"
        )
      }),
      fevg: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.fevg"
        )
      }),
      systolic: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.systolic"
        )
      }),
      diastolic: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.diastolic"
        )
      }),
      severeHypoglycaemia: validateInclusion({
        list: ["yes", "no", "unknown"],
        allowBlank: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.severe-hypoglycaemia"
        )
      }),
      severeHyperglycaemia: validateInclusion({
        list: ["yes", "no", "unknown"],
        allowBlank: true,
        description: intl.t(
          "validations.diabetowise-discharge-completed-journal-event.severe-hyperglycaemia"
        )
      })
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", {
    async: false,
    inverse: "diabetowiseDischargeCompletedJournalEvent"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "diabetowise-discharge-completed-journal-event": DiabetowiseDischargeCompletedJournalEvent;
  }
}
